import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@mui/icons-material";
import Decimal from "decimal.js";
import { getActiveDrawdownContract } from "../../helpers/drawdownContracts";

const MixTable = ({ portfolio, projects }) => {
  const mix = portfolio.currentMixVersion?.MixAllocations || [];
  if (!mix.length) { return <Typography variant="body2" color="text.secondary">No mix set</Typography>; }
  const hasAbsolute = mix.some((m) => m.targetAbsoluteTons != null);
  return (
    <Table size="small" sx={{ minWidth: 260 }}>
      <TableHead>
        <TableRow sx={{ "& th": { fontWeight: "bold", bgcolor: "grey.100", py: 0.5 } }}>
          <TableCell>Project</TableCell>
          <TableCell align="right">{hasAbsolute ? "Tons" : "Mix %"}</TableCell>
          <TableCell align="right">Cost/Ton</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {mix.map((m) => {
          const proj = projects.find((p) => String(p.id) === String(m.projectId));
          const val = hasAbsolute
            ? `${m.targetAbsoluteTons}`
            : `${m.targetPercentageTon}%`;
          const cost = m.projectCostPerTon != null
            ? `$${new Decimal(m.projectCostPerTon).toFixed(2)}`
            : "—";
          return (
            <TableRow key={m.projectId}>
              <TableCell sx={{ py: 0.5 }}>{proj?.projectName || m.projectId}</TableCell>
              <TableCell align="right" sx={{ py: 0.5 }}>{val}</TableCell>
              <TableCell align="right" sx={{ py: 0.5 }}>{cost}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

/*
  Props:
  - portfolios: { id, portfolioKey, portfolioName, inventoryPoolId, InventoryPool,
      currentPrice, currentMixVersion (with MixAllocations),
      calculatedCostPerTon, margin, tonsAvailable }[]
  - clients: { id, client }[]
  - inventoryPools: { id, inventoryPool }[]
  - projects: { id, projectKey, projectName }[]
  - contracts: { id, projectId, Allocations: [{ inventoryPoolId }] }[]
      — used only to filter the project dropdown by pool
  - onAddPortfolio({ portfolioKey, portfolioName, inventoryPoolId, pricePerTon,
      projectMix: [{ projectId, targetPercentageTon, targetAbsoluteTons }], clients })
  - onUpdatePortfolioMix({ id, data: { projectMix } })
  - onAddClient({ client })
*/
export function PortfoliosSection({
  portfolios,
  clients,
  inventoryPools,
  projects,
  contracts = [],
  onAddPortfolio,
  onUpdatePortfolioMix,
  onUpdatePortfolioPrice,
  onAddPortfolioClients,
  onAddClient
}) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);

  const [portfolioName, setPortfolioName] = useState("");
  const [selectedPool, setSelectedPool] = useState("");
  const [pricePerTon, setPricePerTon] = useState("");
  const [mixMode, setMixMode] = useState("percentage");
  const [projectMix, setProjectMix] = useState([]);
  const [mixError, setMixError] = useState("");
  const [targetTotal, setTargetTotal] = useState("100");
  const [selectedClients, setSelectedClients] = useState([]);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState("");

  const [editMixMode, setEditMixMode] = useState("percentage");
  const [editProjectMix, setEditProjectMix] = useState([]);
  const [editMixError, setEditMixError] = useState("");
  const [editTargetTotal, setEditTargetTotal] = useState("100");

  const [isEditPriceOpen, setIsEditPriceOpen] = useState(false);
  const [editingPricePortfolio, setEditingPricePortfolio] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState("");

  const [isAddClientsOpen, setIsAddClientsOpen] = useState(false);
  const [addClientsPortfolio, setAddClientsPortfolio] = useState(null);
  const [addClientsList, setAddClientsList] = useState([]);
  const [showNewClientInAdd, setShowNewClientInAdd] = useState(false);
  const [newClientNameInAdd, setNewClientNameInAdd] = useState("");

  const [portfolioReviewOpen, setPortfolioReviewOpen] = useState(false);
  const [pendingPortfolio, setPendingPortfolio] = useState(null);

  const [expandedPools, setExpandedPools] = useState(new Set());
  const initializedPools = useRef(false);
  useEffect(() => {
    if (!initializedPools.current && inventoryPools.length > 0) {
      initializedPools.current = true;
      setExpandedPools(new Set(inventoryPools.map((p) => p.id)));
    }
  }, [inventoryPools]);

  const togglePool = (poolId) => {
    const next = new Set(expandedPools);
    if (next.has(poolId)) { next.delete(poolId); } else { next.add(poolId); }
    setExpandedPools(next);
  };

  const toKey = (name) =>
    name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

  const resetForm = () => {
    setPortfolioName("");
    setSelectedPool("");
    setPricePerTon("");
    setMixMode("percentage");
    setProjectMix([]);
    setMixError("");
    setTargetTotal("100");
    setSelectedClients([]);
    setShowNewClient(false);
    setNewClientName("");
  };

  const getProjectsForPool = (poolId) => {
    if (!poolId) { return projects; }
    return projects.filter((p) =>
      contracts.some(
        (c) =>
          String(c.projectId) === String(p.id) &&
          !c.voidedOn &&
          (c.totalTons > (c.tonsSold ?? 0)) &&
          (c.Allocations || c.allocations || []).some(
            (a) => String(a.inventoryPoolId) === String(poolId)
          )
      )
    );
  };

  const normalizeContractsForDrawdown = (contractsList) =>
    contractsList.map((c) => ({
      ...c,
      Allocations: c.Allocations || c.allocations || []
    }));

  const buildTonsSoldByContract = (contractsList) => {
    const map = {};
    contractsList.forEach((c) => { map[c.id] = c.tonsSold || 0; });
    return map;
  };

  const computeCalcCostPerTon = (mixItems, mode, poolId, contractsList) => {
    const items = mixItems.filter((pm) => pm.projectId && pm.value !== "" && pm.value != null);
    if (!items.length) { return null; }
    const normalizedContracts = normalizeContractsForDrawdown(contractsList);
    const tonsSoldByContract = buildTonsSoldByContract(normalizedContracts);
    if (mode === "percentage") {
      let total = new Decimal(0);
      for (const pm of items) {
        const contract = getActiveDrawdownContract(normalizedContracts, pm.projectId, poolId, tonsSoldByContract);
        if (!contract) { return null; }
        total = total.plus(
          new Decimal(pm.value || 0)
            .dividedBy(100)
            .times(new Decimal(contract.pricePerTon))
        );
      }
      return total.toNumber();
    }
    let weightedSum = new Decimal(0);
    let totalTons = new Decimal(0);
    for (const pm of items) {
      const contract = getActiveDrawdownContract(normalizedContracts, pm.projectId, poolId, tonsSoldByContract);
      if (!contract) { return null; }
      const tons = new Decimal(parseFloat(pm.value) || 0);
      weightedSum = weightedSum.plus(tons.times(new Decimal(contract.pricePerTon)));
      totalTons = totalTons.plus(tons);
    }
    return totalTons.greaterThan(0) ? weightedSum.dividedBy(totalTons).toNumber() : null;
  };

  const buildMixPayload = (mix, mode) =>
    mix
      .filter((pm) => pm.projectId && pm.value !== "")
      .map((pm) => ({
        projectId: pm.projectId,
        targetPercentageTon: mode === "percentage" ? new Decimal(pm.value || 0).toNumber() : null,
        targetAbsoluteTons: mode === "tonnage" ? new Decimal(pm.value || 0).toNumber() : null
      }));

  const getPercentageMixError = (mix, target) => {
    if (target === "" || new Decimal(target).lessThan(100)) {
      return "Target total must be at least 100%";
    }
    if (mix.length === 0) { return ""; }
    const total = mix.reduce(
      (s, pm) => s.plus(new Decimal(pm.value || 0)),
      new Decimal(0)
    );
    if (!total.equals(new Decimal(target))) {
      return `Total must equal the target total of ${target}% (current: ${total}%)`;
    }
    return "";
  };

  const buildTargetTotalPercentage = (target, mode) =>
    mode === "percentage" ? new Decimal(target).toNumber() : null;

  const handleCreatePortfolio = () => {
    if (!portfolioName || !selectedPool || !pricePerTon || parseFloat(pricePerTon) <= 0) { return; }
    const portfolioKey = toKey(portfolioName);
    if (mixMode === "percentage") {
      const error = getPercentageMixError(projectMix, targetTotal);
      if (error) {
        setMixError(error);
        return;
      }
    }
    const poolName = inventoryPools.find((p) => p.id === selectedPool)?.inventoryPool || "";
    const mixPayload = buildMixPayload(projectMix, mixMode);
    const mixWithNames = mixPayload.map((pm) => ({
      ...pm,
      projectName: projects.find((p) => String(p.id) === String(pm.projectId))?.projectName || String(pm.projectId)
    }));
    const clientsWithNames = selectedClients.map((c) => ({
      ...c,
      clientName: clients.find((cl) => cl.id === c.clientId)?.client || String(c.clientId)
    }));
    setPendingPortfolio({
      portfolioKey,
      portfolioName,
      inventoryPoolId: selectedPool,
      poolName,
      pricePerTon: new Decimal(pricePerTon).toNumber(),
      projectMix: mixPayload,
      targetTotalPercentage: buildTargetTotalPercentage(targetTotal, mixMode),
      mixType: mixMode,
      mixWithNames,
      mixMode,
      clients: selectedClients,
      clientsWithNames
    });
    setPortfolioReviewOpen(true);
  };

  const handlePortfolioConfirm = () => {
    const { poolName, mixWithNames, mixMode, clientsWithNames, ...portfolioPayload } = pendingPortfolio;
    onAddPortfolio(portfolioPayload);
    setPortfolioReviewOpen(false);
    setIsCreateOpen(false);
    resetForm();
    setPendingPortfolio(null);
  };

  const handlePortfolioReviewBack = () => {
    setPortfolioReviewOpen(false);
    setPendingPortfolio(null);
  };

  const openAddClientsDialog = (portfolio) => {
    setAddClientsPortfolio(portfolio);
    setAddClientsList([]);
    setShowNewClientInAdd(false);
    setNewClientNameInAdd("");
    setIsAddClientsOpen(true);
  };

  const handleAddPortfolioClients = () => {
    if (!addClientsPortfolio || addClientsList.length === 0) { return; }
    onAddPortfolioClients({ id: addClientsPortfolio.id, clients: addClientsList.map((id) => ({ clientId: id, isPrimary: false })) });
    setIsAddClientsOpen(false);
    setAddClientsPortfolio(null);
    setAddClientsList([]);
  };

  const openEditMixDialog = (portfolio) => {
    setEditingPortfolio(portfolio);
    const version = portfolio.currentMixVersion;
    const latestMix = version?.MixAllocations || [];
    const hasAbsolute = version?.mixType
      ? version.mixType === "tonnage"
      : latestMix.some((m) => m.targetAbsoluteTons != null);
    setEditMixMode(hasAbsolute ? "tonnage" : "percentage");
    setEditTargetTotal(String(version?.targetTotalPercentage ?? 100));
    setEditProjectMix(
      latestMix.map((m) => ({
        projectId: m.projectId,
        value: hasAbsolute
          ? String(m.targetAbsoluteTons ?? "")
          : String(m.targetPercentageTon ?? "")
      }))
    );
    setEditMixError("");
    setIsEditOpen(true);
  };

  const openEditPriceDialog = (portfolio) => {
    setEditingPricePortfolio(portfolio);
    const current = portfolio.currentPrice?.pricePerTon;
    setEditPriceValue(current != null ? String(current) : "");
    setIsEditPriceOpen(true);
  };

  const handleUpdatePortfolioPrice = () => {
    if (!editingPricePortfolio || !editPriceValue || parseFloat(editPriceValue) <= 0) { return; }
    onUpdatePortfolioPrice({
      id: editingPricePortfolio.id,
      data: { pricePerTon: new Decimal(editPriceValue).toNumber() }
    });
    setIsEditPriceOpen(false);
    setEditingPricePortfolio(null);
    setEditPriceValue("");
  };

  const handleUpdatePortfolioMix = () => {
    if (!editingPortfolio) { return; }
    if (editMixMode === "percentage") {
      const error = getPercentageMixError(editProjectMix, editTargetTotal);
      if (error) {
        setEditMixError(error);
        return;
      }
    }
    onUpdatePortfolioMix({
      id: editingPortfolio.id,
      data: {
        projectMix: buildMixPayload(editProjectMix, editMixMode),
        targetTotalPercentage: buildTargetTotalPercentage(editTargetTotal, editMixMode),
        mixType: editMixMode
      }
    });
    setIsEditOpen(false);
    setEditingPortfolio(null);
  };

  const renderMixEditor = (mix, setMix, mode, setMode, onModeChange, poolId, error, target, setTarget) => {
    const availableProjects = getProjectsForPool(poolId);
    const percentageTotal = mix.reduce(
      (s, pm) => s.plus(new Decimal(pm.value || 0)),
      new Decimal(0)
    );
    const tonnageTotal = mix.reduce(
      (s, pm) => s.plus(new Decimal(pm.value || 0)),
      new Decimal(0)
    );
    const calcCost = computeCalcCostPerTon(mix, mode, poolId, contracts);
    const selectedProjectIds = new Set(
      mix.map((pm) => pm.projectId).filter(Boolean)
    );

    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2">Project Mix</Typography>
          <ToggleButtonGroup value={mode} exclusive onChange={onModeChange} size="small">
            <ToggleButton value="percentage">Percentage</ToggleButton>
            <ToggleButton value="tonnage">Tonnage</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {mix.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center">
              No projects added yet
            </Typography>
          )}
          {mix.map((pm, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Select project</InputLabel>
                <Select
                  value={pm.projectId}
                  label="Select project"
                  onChange={(e) => {
                    const updated = [...mix];
                    updated[idx] = { ...updated[idx], projectId: e.target.value };
                    setMix(updated);
                  }}
                >
                  {availableProjects
                    .filter((p) => !selectedProjectIds.has(p.id) || p.id === pm.projectId)
                    .map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                type="number"
                size="small"
                sx={{ width: 100 }}
                placeholder="0"
                inputProps={{ min: 0 }}
                value={pm.value}
                onChange={(e) => {
                  const updated = [...mix];
                  updated[idx] = { ...updated[idx], value: e.target.value };
                  setMix(updated);
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ width: 24 }}>
                {mode === "percentage" ? "%" : "t"}
              </Typography>
              <Button size="small" onClick={() => setMix(mix.filter((_, i) => i !== idx))}>
                ×
              </Button>
            </Box>
          ))}
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setMix([...mix, { projectId: "", value: "" }])}
          >
            Add Project
          </Button>
          {mode === "percentage" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, borderTop: 1, borderColor: "divider", pt: 1 }}>
              <Typography variant="body2">Target total:</Typography>
              <TextField
                type="number"
                size="small"
                sx={{ width: 100 }}
                inputProps={{ min: 100, "aria-label": "Target total" }}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              />
              <Typography variant="body2" color="text.secondary">%</Typography>
            </Box>
          )}
          {mode === "percentage" && target !== "" && new Decimal(target).greaterThan(100) && (
            <Typography variant="body2" color="warning.main">
              Purchases on this portfolio will retire more tons than they sell. Make sure the price per ton covers it.
            </Typography>
          )}
          {mode === "percentage" && mix.length > 0 && (
            <Typography variant="body2">
              Total: {percentageTotal.toString()}%
            </Typography>
          )}
          {mode === "tonnage" && mix.length > 0 && (
            <Typography variant="body2" sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}>
              Tons retired per purchase: {tonnageTotal.toString()}t
            </Typography>
          )}
          {calcCost != null && (
            <Typography variant="body2">
              Calculated Cost/Ton: ${new Decimal(calcCost).toFixed(2)}
            </Typography>
          )}
          {error && <Typography variant="body2" color="error">{error}</Typography>}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Portfolios</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)}>
          Add Portfolio
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {(() => {
          const knownPoolIds = new Set(inventoryPools.map((p) => String(p.id)));
          const uncategorized = portfolios.filter((p) => !knownPoolIds.has(String(p.inventoryPoolId)));
          if (uncategorized.length === 0) { return null; }
          return (
            <Card>
              <CardHeader title={<Typography variant="h4" color="warning.main">Uncategorized</Typography>} />
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {uncategorized.map((p) => p.portfolioName).join(", ")}
                </Typography>
              </CardContent>
            </Card>
          );
        })()}
        {inventoryPools.map((pool) => {
          const poolPortfolios = portfolios.filter(
            (p) => String(p.inventoryPoolId) === String(pool.id)
          );
          const expanded = expandedPools.has(pool.id);
          return (
            <Card key={pool.id}>
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => togglePool(pool.id)}
                      sx={{ minWidth: 32, p: 0.5 }}
                    >
                      {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Button>
                    <Typography variant="h4">{pool.inventoryPool}</Typography>
                  </Box>
                }
              />
              {expanded && (
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Target Price/Ton</TableCell>
                          <TableCell>Calc. Cost/Ton</TableCell>
                          <TableCell>Margin</TableCell>
                          <TableCell>Tons Available</TableCell>
                          <TableCell>Project Mix</TableCell>
                          <TableCell>Clients</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {poolPortfolios.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ color: "text.secondary" }}>
                              No portfolios
                            </TableCell>
                          </TableRow>
                        )}
                        {poolPortfolios.map((portfolio) => {
                          const currentPrice = portfolio.currentPrice?.pricePerTon != null
                            ? new Decimal(portfolio.currentPrice.pricePerTon)
                            : null;
                          const { calculatedCostPerTon, margin, tonsAvailable } = portfolio;

                          return (
                            <TableRow key={portfolio.id}>
                              <TableCell>{portfolio.portfolioName}</TableCell>
                              <TableCell>
                                {currentPrice != null ? `$${currentPrice.toFixed(2)}` : "—"}
                              </TableCell>
                              <TableCell>
                                {calculatedCostPerTon != null
                                  ? `$${new Decimal(calculatedCostPerTon).toFixed(2)}`
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                {margin != null
                                  ? (
                                    <Typography
                                      variant="body2"
                                      sx={{ color: margin >= 0 ? "success.main" : "error.main" }}
                                    >
                                      {new Decimal(margin).toFixed(1)}%
                                    </Typography>
                                  )
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                {tonsAvailable != null
                                  ? new Decimal(tonsAvailable).toNumber().toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                <MixTable portfolio={portfolio} projects={projects} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {(portfolio.Clients || []).map((c) => {
                                    const isPrimary = c.CarbonOffsetClientPortfolio?.isPrimary;
                                    return isPrimary ? `${c.client} (primary)` : c.client;
                                  }).join(", ") || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => openEditPriceDialog(portfolio)}
                                >
                                  Target Price/Ton
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => openEditMixDialog(portfolio)}
                                >
                                  Mix
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => openAddClientsDialog(portfolio)}
                                >
                                  Clients
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </CardContent>
              )}
            </Card>
          );
        })}
      </Box>

      <Dialog open={portfolioReviewOpen} onClose={handlePortfolioReviewBack} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm New Portfolio</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please review the portfolio details before submitting.
          </Typography>
          {pendingPortfolio && (
            <Box>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", border: 0, pl: 0 }}>Portfolio Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, border: 0 }}>{pendingPortfolio.portfolioName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", border: 0, pl: 0 }}>Inventory Pool</TableCell>
                    <TableCell sx={{ border: 0 }}>{pendingPortfolio.poolName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: "text.secondary", border: 0, pl: 0 }}>Price per Ton</TableCell>
                    <TableCell sx={{ border: 0 }}>${new Decimal(pendingPortfolio.pricePerTon).toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {pendingPortfolio.mixWithNames.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
                    Project Mix
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ pl: 0 }}>Project</TableCell>
                        <TableCell align="right">{pendingPortfolio.mixMode === "percentage" ? "%" : "Tons"}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingPortfolio.mixWithNames.map((pm) => (
                        <TableRow key={pm.projectId}>
                          <TableCell sx={{ pl: 0 }}>{pm.projectName}</TableCell>
                          <TableCell align="right">
                            {pendingPortfolio.mixMode === "percentage"
                              ? `${pm.targetPercentageTon}%`
                              : pm.targetAbsoluteTons}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {(() => {
                    const calcCost = computeCalcCostPerTon(
                      pendingPortfolio.mixWithNames.map((pm) => ({
                        projectId: pm.projectId,
                        value: pendingPortfolio.mixMode === "percentage"
                          ? pm.targetPercentageTon
                          : pm.targetAbsoluteTons
                      })),
                      pendingPortfolio.mixMode,
                      pendingPortfolio.inventoryPoolId,
                      contracts
                    );
                    return calcCost != null
                      ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Calculated Cost/Ton: ${new Decimal(calcCost).toFixed(2)}
                        </Typography>
                      )
                      : null;
                  })()}
                </Box>
              )}
              {pendingPortfolio.clientsWithNames.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
                    Clients
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ pl: 0 }}>Client</TableCell>
                        <TableCell align="right">Primary</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingPortfolio.clientsWithNames.map((c) => (
                        <TableRow key={c.clientId}>
                          <TableCell sx={{ pl: 0 }}>{c.clientName}</TableCell>
                          <TableCell align="right">{c.isPrimary ? "Yes" : "No"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePortfolioReviewBack}>← Go Back</Button>
          <Button variant="contained" onClick={handlePortfolioConfirm}>Confirm &amp; Create Portfolio</Button>
        </DialogActions>
      </Dialog>

      {/* Create portfolio dialog */}
      <Dialog open={isCreateOpen} onClose={() => { setIsCreateOpen(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>Create New Portfolio</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Portfolio Name"
              size="small"
              value={portfolioName}
              onChange={(e) => setPortfolioName(e.target.value)}
            />

            <FormControl size="small">
              <InputLabel>Inventory Pool</InputLabel>
              <Select
                value={selectedPool}
                label="Inventory Pool"
                onChange={(e) => { setSelectedPool(e.target.value); setProjectMix([]); }}
              >
                {inventoryPools.map((pool) => (
                  <MenuItem key={pool.id} value={pool.id}>{pool.inventoryPool}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Target Price/Ton"
              type="text"
              size="small"
              value={pricePerTon}
              onChange={(e) => setPricePerTon(e.target.value)}
              onBlur={() => {
                try { setPricePerTon(new Decimal(pricePerTon).toFixed(2)); } catch (e) {}
              }}
              onFocus={() => {
                try { setPricePerTon(new Decimal(pricePerTon).toString()); } catch (e) {}
              }}
            />

            {renderMixEditor(
              projectMix, setProjectMix,
              mixMode, setMixMode,
              (_, v) => { if (v) { setMixMode(v); setProjectMix(projectMix.map((pm) => ({ ...pm, value: "" }))); } },
              selectedPool,
              mixError,
              targetTotal, setTargetTotal
            )}

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Clients</Typography>
              {selectedClients.map((sc, idx) => {
                const clientObj = clients.find((c) => c.id === sc.clientId);
                return (
                  <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography sx={{ flex: 1 }}>{clientObj?.client || sc.clientId}</Typography>
                    <IconButton size="small" onClick={() => setSelectedClients(selectedClients.filter((_, i) => i !== idx))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
              {!showNewClient
                ? (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select client</InputLabel>
                      <Select
                        value=""
                        label="Select client"
                        onChange={(e) => {
                          if (!selectedClients.find((sc) => sc.clientId === e.target.value)) {
                            setSelectedClients([...selectedClients, { clientId: e.target.value, isPrimary: false }]);
                          }
                        }}
                      >
                        {clients
                          .filter((c) => !selectedClients.find((sc) => sc.clientId === c.id))
                          .map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.client}</MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={() => setShowNewClient(true)} sx={{ minWidth: 40 }}>
                      <AddIcon />
                    </Button>
                  </Box>
                )
                : (
                  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      label="Client name"
                      size="small"
                      value={newClientName}
                      onChange={(e) => setNewClientName(e.target.value)}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!newClientName}
                        onClick={async () => {
                          const created = await onAddClient({ client: newClientName });
                          setNewClientName("");
                          setShowNewClient(false);
                          if (created?.id) {
                            setSelectedClients([...selectedClients, { clientId: created.id, isPrimary: false }]);
                          }
                        }}
                      >
                        Add
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => { setShowNewClient(false); setNewClientName(""); }}>Cancel</Button>
                    </Box>
                  </Box>
                )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsCreateOpen(false); resetForm(); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePortfolio}>Create Portfolio</Button>
        </DialogActions>
      </Dialog>

      {/* Edit mix dialog */}
      <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Adjust Project Mix — {editingPortfolio?.portfolioName}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            {renderMixEditor(
              editProjectMix, setEditProjectMix,
              editMixMode, setEditMixMode,
              (_, v) => { if (v) { setEditMixMode(v); setEditProjectMix(editProjectMix.map((pm) => ({ ...pm, value: "" }))); } },
              editingPortfolio?.inventoryPoolId,
              editMixError,
              editTargetTotal, setEditTargetTotal
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdatePortfolioMix}>Update Project Mix</Button>
        </DialogActions>
      </Dialog>

      {/* Add clients dialog */}
      <Dialog open={isAddClientsOpen} onClose={() => setIsAddClientsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Clients — {addClientsPortfolio?.portfolioName}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>
            {addClientsList.map((clientId, idx) => {
              const clientObj = clients.find((c) => c.id === clientId);
              return (
                <Box key={clientId} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ flex: 1 }}>{clientObj?.client || clientId}</Typography>
                  <IconButton size="small" onClick={() => setAddClientsList(addClientsList.filter((_, i) => i !== idx))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
            {!showNewClientInAdd
              ? (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select client</InputLabel>
                    <Select
                      value=""
                      label="Select client"
                      onChange={(e) => {
                        const existingIds = (addClientsPortfolio?.Clients || []).map((c) => c.id);
                        if (!existingIds.includes(e.target.value) && !addClientsList.includes(e.target.value)) {
                          setAddClientsList([...addClientsList, e.target.value]);
                        }
                      }}
                    >
                      {clients
                        .filter((c) => {
                          const existingIds = (addClientsPortfolio?.Clients || []).map((ec) => ec.id);
                          return !existingIds.includes(c.id) && !addClientsList.includes(c.id);
                        })
                        .map((c) => (
                          <MenuItem key={c.id} value={c.id}>{c.client}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" onClick={() => setShowNewClientInAdd(true)} sx={{ minWidth: 40 }}>
                    <AddIcon />
                  </Button>
                </Box>
              )
              : (
                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField
                    label="Client name"
                    size="small"
                    value={newClientNameInAdd}
                    onChange={(e) => setNewClientNameInAdd(e.target.value)}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!newClientNameInAdd}
                      onClick={async () => {
                        const created = await onAddClient({ client: newClientNameInAdd });
                        setNewClientNameInAdd("");
                        setShowNewClientInAdd(false);
                        if (created?.id) {
                          setAddClientsList([...addClientsList, created.id]);
                        }
                      }}
                    >
                      Add
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => { setShowNewClientInAdd(false); setNewClientNameInAdd(""); }}>Cancel</Button>
                  </Box>
                </Box>
              )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddClientsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPortfolioClients} disabled={addClientsList.length === 0}>
            Add Clients
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit price dialog */}
      <Dialog open={isEditPriceOpen} onClose={() => setIsEditPriceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Target Price/Ton — {editingPricePortfolio?.portfolioName}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Target Price per Ton"
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0 }}
              value={editPriceValue}
              onChange={(e) => setEditPriceValue(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditPriceOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdatePortfolioPrice}
            disabled={!editPriceValue || isNaN(parseFloat(editPriceValue)) || parseFloat(editPriceValue) <= 0}
          >
            Update Price
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
